import { DatePipe } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ModuleManager } from 'src/shared/models/module-manager';

@Component({
    selector: 'cv-call-arrival-settings',
    templateUrl: './call-arrival-settings.component.html',
    styleUrls: ['./call-arrival-settings.component.scss']
})
export class CallArrivalSettingsComponent implements OnInit {

    @Input() moduleManager: ModuleManager;

    _localizationStringMap;
    _localizationsLoaded = false;
    settings = {
        hybridModeChecked: true,
        selectedHour: 4,
        defaultHours: 4,
        useChosenDateTime: false,
        chosenDateTime: new Date(),
        alignColorsToAgentStates: false,
    };
    // for date picker
    startDate = new Date().addDays(-7);    // for time picker
    today = new Date();
    selectedTime = null;

    hourList = [];
    dateTimeDisplay = "Current";
    dateFormat = CVAngularDateTimeFormat;

    constructor(
        private cvLocalizationService: CVLocalizationService,
        private datePipe: DatePipe,
    ) { }

    async loadLocalization() {
        // Load localizations for this module:
        try {
            this._localizationStringMap = await this.cvLocalizationService.GetSection("Call_Arrival_Module");
            this._localizationsLoaded = true;
        } catch (error) {
            this._localizationStringMap = new Map();
            console.warn("Could not load localizations for section 'Call_Arrival_Module'");
            throw error;
        }
    }

    ngOnInit(): void {

        this.loadLocalization();

        this.moduleManager.addSetting({
            name: 'HourInterval',
            getValue: () => Number.isInteger(this.settings.selectedHour) ? this.settings.selectedHour : this.settings.defaultHours,
            onSettingLoaded: value => {
                this.settings.selectedHour = Number(value);
                if (value === "")
                    this.settings.selectedHour = Number(this.settings.defaultHours);
            },
            defaultValue: this.settings.defaultHours,
        });

        this.moduleManager.addSetting({
            name: 'UseEndDateTime', // linked to useChosenDateTime
            getValue: () => this.settings.useChosenDateTime,
            onSettingLoaded: value => this.settings.useChosenDateTime = value,
            defaultValue: false
        });

        this.moduleManager.addSetting({
            name: 'EndDateTime', // linked to chosenDateTime
            getValue: () => new Date(this.settings.chosenDateTime.getTime() - (this.settings.chosenDateTime.getTimezoneOffset() * 60 * 1000)).toISOString().slice(0, -1), // keep timezone and remove trailing 'Z',
            onSettingLoaded: value => {
                const minDateTime = new Date().addDays(-7);
                if (value < minDateTime) this.settings.chosenDateTime = new Date();
                else {
                    try {
                        this.settings.chosenDateTime = new Date(value);
                    }
                    catch (e) {
                        this.settings.chosenDateTime = new Date();
                    }
                }

                this.selectedTime = new Date(this.settings.chosenDateTime.getTime());
                this.updateDateTimeUI();
            },
            defaultValue: null
        });

        this.moduleManager.addSetting({
            name: 'AlignColorsToAgentStates',
            getValue: () => this.settings.alignColorsToAgentStates,
            onSettingLoaded: value => this.settings.alignColorsToAgentStates = value,
            defaultValue: false
        });

        this.setupHourList();
        this.moduleManager.customSettingsAdded = true;
    }

    /**
     * Returns the value from _localizationStringMap, if it exists.  Otherwise, returns `defaultValue`
     *
     * @param {*} key Key to look up in _localizationStringMap
     * @param {*} defaultValue String to return if `key` isn't in _localizationStringMap
     */

    getLocalizationString(key, defaultValue) {
        let value;
        if (this._localizationStringMap && typeof (value = this._localizationStringMap.get(key)) === "string")
            return value;
        return defaultValue;
    }

    setupHourList() {
        for (let hour = 1; hour <= 72; hour++) {
            this.hourList.push(hour);
        }
    };

    // used to control date and time picker based on whether the checkbox is checked or not
    toggleUseEndDateTime() {
        this.updateDateTimeUI();
    };

    onCustomDateRangeChanged() {
        this.settings.useChosenDateTime = true;
        if (this.settings.chosenDateTime === null) this.settings.chosenDateTime = new Date();
        else this.settings.chosenDateTime = this.settings.chosenDateTime;
        this.updateDateTimeUI();
    }

    onSelectedTimeChanged(date: Date) {
        this.settings.useChosenDateTime = true;
        this.settings.chosenDateTime.setHours(date.getHours(), date.getMinutes());
        this.updateDateTimeUI();
    }

    updateDateTimeUI() {
        if (this.settings.useChosenDateTime) {
            if (this.settings.chosenDateTime === null) this.settings.chosenDateTime = new Date();
            this.dateTimeDisplay = this.settings.chosenDateTime.toString();
        }
        else {
            this.dateTimeDisplay = this.getLocalizationString("Current", "Current");
        }
    };

    /**
     * Returns the localized text to display in the Context line of the Slideout Settings section
     * "Will show HOURS_PRIOR hours prior to END_DATE"
     */
    getSlideoutContextSectionText() {
        // Before localizations are loaded, don't even show the fallback string:
        if (!this._localizationsLoaded)
            return "";

        let contextSectionText;
        if (this.settings.selectedHour === 1) {
            contextSectionText = this.cvLocalizationService.Format(
                this.getLocalizationString("Slideout_Context_Singular",
                    "Will show 1 hour prior to {0}.") , this.getDateDisplayValue());
        } else {
            // The string is expected to have the two replaceable strings "HOURS_PRIOR" and "END_DATE".
            contextSectionText = this.cvLocalizationService.Format(
                this.getLocalizationString("Slideout_Context_Plural",
                    "Will show {0} hours prior to {1}."), this.settings.selectedHour, this.getDateTimeDisplayValue());
            // contextSectionText = contextSectionText.replace("HOURS_PRIOR", this.settings.selectedHour || this.settings.defaultHours);
        }
        // contextSectionText = contextSectionText.replace("END_DATE", this.getDateTimeDisplayValue());
        ;
        return contextSectionText;
    };

    /**
     * When selectedHour is invalid, it sets the value of selectedHour to the default.
     * Slideout settings uses an <input type="number"> instead of the <select> of the legacy settings.
     * Restrictions on the <input> element, when they don't pass, cause the browser to set the value to undefined.
     * Called by ngBlur in the template
     */
    resetInvalidHoursToDefault() {
        if (!Number.isInteger(this.settings.selectedHour) || this.settings.selectedHour < 1 || this.settings.selectedHour > 72)
            this.settings.selectedHour = this.settings.defaultHours;
    };

    /**
     * Returns the string of the configured date, if the "prior to" checkbox is checked.  Otherwise returns "Current"
     * Remove or update when the date pickers are ready
     * Used in the template to display the value of the date picker
     */
    getDateDisplayValue() {
        if (!this.settings.useChosenDateTime)
            return this.getLocalizationString("Current", "Current");
        return this.datePipe.transform(this.settings.chosenDateTime, CVAngularDateFormat);
    };

    /**
     * Returns the string of the configured time, if the "prior to" checkbox is checked.  Otherwise returns "Current"
     * Remove or update when the date pickers are ready
     * Used in the template to display the value of the time picker
     */
    getTimeDisplayValue() {
        if (!this.settings.chosenDateTime)
            return this.getLocalizationString("Current", "Current");
        return this.datePipe.transform(this.settings.chosenDateTime, CVAngularTimeFormat);
    };

    /**
     * Returns the string of the currently configured date and time, if the "prior to" checkbox is checked.  Otherwise, returns "current".
     */
    getDateTimeDisplayValue() {
        if (!this.settings.useChosenDateTime)
            return this.getLocalizationString("Current_Lowercase", "current");
        return this.datePipe.transform(this.settings.chosenDateTime, CVAngularDateTimeFormat);
    }
}
